/** @format */

import { useTranslation } from "react-i18next";
import type { Brand } from "@question-bank/shared";
import type { AnswerLinesConfig, PdfQuestion } from "../types";
import {
	A4_MIN_HEIGHT,
	A4_WIDTH,
	BODY_FONT_SIZE,
	COLOR_SOURCE,
	COLOR_TEXT,
	FOOTNOTE_FONT_SIZE,
	formatPaperNumber,
	isMergedHeader,
	PAGE_PADDING_BOTTOM,
	PAGE_PADDING_HORIZONTAL,
	PAGE_PADDING_TOP,
	partLabel,
	pt,
	SOURCE_MARGIN_BOTTOM,
	SOURCE_MARGIN_TOP,
} from "../layout";
import { QuestionContent } from "./QuestionContent";

interface QuestionSheetProps {
	group: PdfQuestion[];
	groupIndex: number;
	brand: Brand;
	answerLines: AnswerLinesConfig;
}

/**
 * HTML twin of one export '<Page>' 'pdf/QuestionsPage' QB2-122: an a4- width sheet per question
 * group with exports page padding, so wrapping approx the pdf.
 * Helvetica because the export uses react-pdf's Helvetica. not roboto.
 * minHeight rather than fixed height, react-pdf breaks overflow onto continuation pages, the sheet just stretches instead.
 */

export const QuestionSheet = ({ group, groupIndex, brand, answerLines }: QuestionSheetProps) => {
	const { t } = useTranslation();
	return (
		<section
			data-testid="paper-sheet"
			className="bg-white shadow-md mx-auto max-w-full"
			style={{
				width: A4_WIDTH,
				minHeight: A4_MIN_HEIGHT,
				paddingTop: PAGE_PADDING_TOP,
				paddingBottom: PAGE_PADDING_BOTTOM,
				paddingLeft: PAGE_PADDING_HORIZONTAL,
				paddingRight: PAGE_PADDING_HORIZONTAL,
				color: COLOR_TEXT,
				fontSize: pt(BODY_FONT_SIZE),
				fontFamily: "Helvetica, Arial, sans-serif",
			}}
		>
			{group.map((question, partIndex) => {
				if (isMergedHeader(group, partIndex)) {
					return null;
				}
				const mergedHeader = isMergedHeader(group, partIndex - 1) ? group[partIndex - 1] : undefined;
				const precedingLabels = group.slice(0, partIndex).map((part) => part.displayLabel);
				const isFirstRenderedPart = mergedHeader === undefined ? partIndex === 0 : partIndex === 1;
				return (
					<QuestionContent
						key={question.id}
						question={question}
						paperNumber={isFirstRenderedPart ? formatPaperNumber(groupIndex) : ""}
						precedingLabels={precedingLabels}
						inlineParentLabel={
							mergedHeader === undefined
								? undefined
								: partLabel(mergedHeader.displayLabel, precedingLabels.slice(0, -1))
						}
						isLastPart={partIndex === group.length - 1}
						brand={brand}
						answerLines={answerLines}
					/>
				);
			})}
			{group[0].sourceSeries && (
				<p
					style={{
						marginTop: pt(SOURCE_MARGIN_TOP),
						marginBottom: pt(SOURCE_MARGIN_BOTTOM),
						color: COLOR_SOURCE,
						fontSize: pt(FOOTNOTE_FONT_SIZE),
					}}
				>
					{t("pdfExport.questionTakenFrom", {
						paperCode: group[0].sourcePaperCode,
						season: group[0].sourceSeries.season.title ?? group[0].sourceSeries.season.code,
						year: group[0].sourceSeries.year,
					})}
				</p>
			)}
		</section>
	);
};
